import { useEffect, useRef, useState } from "react";
import { Alert } from "./icons";
import type { ConsoleLine } from "./Preview";

// The preview's console. On a big screen it's the panel under the game; in the compact layout
// (Review 17, C62) it's one line ("Console: no errors") that opens on a click, and opens by itself
// when a new error shows up.

// Before Lesson 4 (D46: line-by-line console messages unlock with "Feedback loops"), the console
// shows one summary line instead of the messages.
export function ConsoleBar({ lines, onSendErrors, compact, errorDetails = true }: { lines: ConsoleLine[]; onSendErrors?: () => void; compact: boolean; errorDetails?: boolean }) {
  const errors = lines.filter((l) => l.level === "error").length;
  const [open, setOpen] = useState(false);
  const seen = useRef(errors);
  useEffect(() => {
    if (errors > seen.current) setOpen(true);
    seen.current = errors;
  }, [errors]);

  const badge = errors > 0 ? <span className="badge bad">{errors} error{errors === 1 ? "" : "s"}</span> : <span className="badge">no errors</span>;
  const list = !errorDetails ? (
    errors > 0 ? (
      <p className="console-line">
        Something in your program went wrong.
        {onSendErrors && (
          <button type="button" className="send-errors" onClick={onSendErrors}>
            Send to helper
          </button>
        )}
      </p>
    ) : null
  ) : (
    <ol>
      {lines.length === 0 && <li className="muted">Messages from console.log and errors show up here.</li>}
      {lines.map((l) => (
        <li key={l.id} className={`line ${l.level}`}>
          {l.level === "error" && <Alert size={16} stroke="#B5452F" />}
          <code>{l.text}</code>
          {l.level === "error" && onSendErrors && (
            <button type="button" className="send-errors" onClick={onSendErrors}>
              Send to helper
            </button>
          )}
        </li>
      ))}
    </ol>
  );

  if (!compact) {
    return (
      <div className={`console${errorDetails ? "" : " console-summary"}`} aria-label="Console">
        <div className="console-head">
          <span className="eyebrow">Console</span>
          {badge}
        </div>
        {list}
      </div>
    );
  }
  const others = errorDetails ? lines.length - errors : 0;
  return (
    <div className={`console console-bar${open ? " open" : ""}${errors ? " has-errors" : ""}`} aria-label="Console">
      <button type="button" className="console-toggle" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="eyebrow">Console:</span>
        {badge}
        {!open && others > 0 && <span className="muted small console-more">{others} message{others === 1 ? "" : "s"}</span>}
        <span className="console-caret" aria-hidden="true">{open ? "▾" : "▸"}</span>
      </button>
      {open && list}
    </div>
  );
}
